// Custom dashboards API client (Part C). Server-backed named dashboard "tabs"
// with private/public/shared visibility + per-user/role sharing. The `config`
// blob holds the same GridItem[] the dashboard layout hook produces.

import { api } from './api';
import type { GridItem } from '@/components/dashboard-grid/useDashboardLayout';

export type DashboardVisibility = 'private' | 'public' | 'shared';

/** Summary row from GET /dashboards (no config). */
export interface DashboardSummary {
  id: string;
  name: string;
  description: string | null;
  visibility: DashboardVisibility;
  isOwner: boolean;
  canEdit: boolean;
  updatedAt: string;
}

/** Full dashboard incl. its layout config. */
export interface DashboardDetail extends DashboardSummary {
  config: { items?: GridItem[] } & Record<string, unknown>;
}

export interface ShareGrant { userId?: string; role?: string; canEdit?: boolean }

export const dashboardsApi = {
  list: async (): Promise<DashboardSummary[]> =>
    (await api.get<{ data: DashboardSummary[] }>('/api/v1/dashboards')).data.data,

  get: async (id: string): Promise<DashboardDetail> =>
    (await api.get<{ data: DashboardDetail }>(`/api/v1/dashboards/${id}`)).data.data,

  create: async (input: { name: string; description?: string; visibility?: DashboardVisibility; config?: Record<string, unknown> }): Promise<DashboardDetail> =>
    (await api.post<{ data: DashboardDetail }>('/api/v1/dashboards', input)).data.data,

  update: async (id: string, patch: Partial<{ name: string; description: string | null; visibility: DashboardVisibility; config: Record<string, unknown> }>): Promise<DashboardDetail> =>
    (await api.patch<{ data: DashboardDetail }>(`/api/v1/dashboards/${id}`, patch)).data.data,

  remove: async (id: string): Promise<void> => {
    await api.delete(`/api/v1/dashboards/${id}`);
  },

  setPermissions: async (id: string, grants: ShareGrant[]): Promise<void> => {
    await api.put(`/api/v1/dashboards/${id}/permissions`, { grants });
  },
};
